import { useCallback } from "react";

import { AccountViewer } from "components/form/graphql/types";
import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Action, Stack } from "types/generated";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";
import useHasStackWriteAccess from "./useHasStackWriteAccess";

const ADD_CONFIG_ACCESS_TYPES = [Action.StackAddConfig];

const hasAddConfigAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(ADD_CONFIG_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can add config to a stack.
 * Stacks created from a template deployment (blueprintDeployment) cannot have config added.
 */
const useCanAddStackConfig = (): ((stack: Stack) => AccessResult) => {
  const hasStackWriteAccess = useHasStackWriteAccess();
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  return useCallback(
    (stack: Stack) => {
      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (hasStackWriteAccess(stack).result) {
        return { result: true };
      }

      if (hasAddConfigAccess(viewer, stack.spaceDetails?.id || stack.space)) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [hasStackWriteAccess, viewer, enableSelfServiceV2]
  );
};

export default useCanAddStackConfig;
